import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart } from "@/hooks/useCart";

const TEXT_COLOR = "#545454";
const ASSET = "https://qclay.design/lovable/bags";

interface ShopNavProps {
  variant?: "light" | "dark";
}

export default function ShopNav({ variant = "light" }: ShopNavProps) {
  const { totalItems } = useCart();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Home page: nav is NEVER fixed — it scrolls away with the page naturally.
  // Other pages: nav is always fixed at the top and shows the white translucent bar.
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (isHomePage) {
      setScrolled(false);
      return;
    }
    setScrolled(true);
  }, [isHomePage]);

  const isDark = variant === "dark";
  const color = scrolled ? TEXT_COLOR : isDark ? "#FFFFFF" : TEXT_COLOR;
  const bg = scrolled ? "rgba(255,255,255,0.94)" : "transparent";
  const borderBottom = scrolled ? "1px solid rgba(84,84,84,0.08)" : "1px solid transparent";

  const links = [
    { label: "Home", to: "/" },
    { label: "Catalog", to: "/catalog" },
    { label: "Story", to: "/story" },
    { label: `Cart (${totalItems})`, to: "/cart" },
  ];

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/catalog") {
      return location.pathname === "/catalog" || location.pathname.startsWith("/product");
    }
    if (to === "/cart") {
      return location.pathname === "/cart" || location.pathname === "/checkout" || location.pathname.startsWith("/order-success");
    }
    return location.pathname === to;
  };

  return (
    <nav
      className="flex items-center justify-end whitespace-nowrap"
      style={{
        position: isHomePage ? "absolute" : "fixed",
        top: 0,
        left: 0,
        right: 0,
        padding: "20px 32px",
        background: bg,
        backdropFilter: scrolled ? "blur(14px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(14px)" : "none",
        borderBottom,
        transition: "background 0.3s ease, border-color 0.3s ease",
        zIndex: 200,
        gap: 32,
      }}
    >
      {links.map((l) => {
        const active = isActive(l.to);
        return (
          <Link
            key={l.to}
            to={l.to}
            className="cursor-pointer transition-opacity duration-200 hover:opacity-60 whitespace-nowrap"
            style={{
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 14,
              fontWeight: 400,
              color,
              textDecoration: active ? "underline" : "none",
              textUnderlineOffset: "4px",
              transition: "color 0.3s ease",
            }}
          >
            {l.label}
          </Link>
        );
      })}
      <button
        type="button"
        aria-label="Menu"
        className="cursor-pointer transition-opacity duration-200 hover:opacity-70"
        style={{ background: "transparent", border: 0, padding: 0 }}
      >
        <img src={`${ASSET}/burger.svg`} alt="" style={{ width: 42, height: 30 }} />
      </button>
    </nav>
  );
}